import { Checkbox, Form, Input, Modal, Row, message } from "antd";
import {
  useAddPacketingRuleMutation,
  useUpdatePacketingRuleMutation,
} from "features/packetingRulesTable/packetingRulesAPI";
import { useMemo, useEffect, useState } from "react";
import { useFetchAllDebtorUdfCustomFieldsQuery } from "features/userDefinedFields/agencyPortal/debtorUserDefinedFieldsAPI";
import styled from "styled-components";
import {
  firstPriorityFields,
  secondPriorityFields,
  thirdPriorityFields,
} from "features/packetingRulesTable/constants";
import { TableFormList } from "components/formList";
import { FormWizard } from "components/formWizard";

const StyledContent = styled.div`
  flex: 4;
  margin-left: 8px;
`;
const StyledRow = styled(Row)`
  display: flex;
  align-items: baseline;
  width: 100%;
`;

export default function PacketingRuleModal({ activeRecord, title, onOk, onCancel }) {
  const [addPacketingRule] = useAddPacketingRuleMutation();
  const [editPacketingRule] = useUpdatePacketingRuleMutation();
  const [criterias, setCriterias] = useState([]);

  // Fetch debtor custom fields
  const { data: debtorCustomFields = [] } = useFetchAllDebtorUdfCustomFieldsQuery();

  // Helper function to transform criteria for display
  const transformCriteriaForDisplay = (criteria, index) => {
    const baseTransformed = {
      ...criteria,
      key: index,
    };

    if (criteria.criteria === "debtor_udf" && criteria.fieldName) {
      return {
        ...baseTransformed,
        criteria: `debtor_udf:${criteria.fieldName}`,
        originalCriteria: "debtor_udf",
      };
    }

    return baseTransformed;
  };

  // Populates criteria during additional load
  useEffect(() => {
    if (activeRecord?.partialMatchParamsScore) {
      const transformedCriterias = activeRecord.partialMatchParamsScore.map((criteria, index) =>
        transformCriteriaForDisplay(criteria, index),
      );
      setCriterias(transformedCriterias);
    }
  }, [activeRecord]);

  const handleDeleteCriteria = (criteria) => {
    setCriterias((prev) => {
      return prev.filter((d) => {
        return d.key !== criteria.key;
      });
    });
  };

  const handleAddCriteria = ({ index, ...criteriaData }) => {
    // Handle the combined debtor_udf:fieldName format
    let processedCriteria = { ...criteriaData };

    if (criteriaData.criteria && criteriaData.criteria.startsWith("debtor_udf:")) {
      const [, fieldSlug] = criteriaData.criteria.split(":");
      processedCriteria = {
        ...criteriaData,
        criteria: "debtor_udf",
        fieldName: fieldSlug,
      };
    }

    if (index === -1) {
      setCriterias((prev) => [...prev, { ...processedCriteria, key: Math.random() }]);
    } else {
      setCriterias((prev) => {
        const copy = [...prev];
        copy[index] = processedCriteria;
        return copy;
      });
    }
    return { data: true };
  };

  const onSubmitForm = async ({ step0, step1, step2 }) => {
    // Clean up criterias before sending
    const cleanedCriterias = criterias.map(({ key: _key, originalCriteria, ...rest }) => {
      // If we have originalCriteria, use it (for edited debtor_udf records)
      if (originalCriteria) {
        return {
          ...rest,
          criteria: originalCriteria,
        };
      }
      return rest;
    });

    const payload = {
      ...step0,
      identifierMatchParams: Object.entries(step1.identifierMatchParams)
        .filter(([, value]) => value)
        .map(([key]) => key),
      disqualifyingMatchParams: Object.entries(step2.disqualifyingMatchParams)
        .filter(([, value]) => value)
        .map(([key]) => key),
      partialMatchParamsScore: cleanedCriterias,
    };

    try {
      const action = activeRecord
        ? editPacketingRule({ id: activeRecord.id, ...payload })
        : addPacketingRule(payload);
      const result = await action;
      if ("data" in result) {
        const successMessage = activeRecord
          ? "Packeting rule updated successfully!"
          : "Packeting rule added successfully!";
        message.success(successMessage);
        onOk();
      }
    } catch (e) {
      message.error("Please make sure all required fields on all tabs are filled out.");
    }
  };

  const initialValues = {
    ...activeRecord,
    identifierMatchParams: activeRecord?.identifierMatchParams.reduce((acc, param) => {
      return { ...acc, [param]: true };
    }, {}),
    disqualifyingMatchParams: activeRecord?.disqualifyingMatchParams.reduce((acc, param) => {
      return { ...acc, [param]: true };
    }, {}),
  };

  // Helper function to get custom field display name
  const getCustomFieldDisplayName = (fieldSlug) => {
    const customField = debtorCustomFields.find((f) => f.slug === fieldSlug);
    return customField?.name || fieldSlug;
  };

  // Combine standard fields with custom field options for debtor_udf (only TEXT and DATE types)
  const criteriaOptions = useMemo(() => {
    const standardOptions = thirdPriorityFields.filter((f) => f.value !== "debtor_udf");

    // Filter custom fields to only include TEXT and DATE types
    const eligibleCustomFields = debtorCustomFields.filter(
      (field) => field.type === "text" || field.type === "date",
    );

    if (eligibleCustomFields.length > 0) {
      const customFieldOptions = eligibleCustomFields.map((field) => ({
        value: `debtor_udf:${field.slug}`,
        label: field.name,
      }));

      const debtorCustomFieldGroup = {
        label: "Debtor Custom Field",
        options: customFieldOptions,
      };

      return [...standardOptions, debtorCustomFieldGroup];
    }

    return standardOptions;
  }, [debtorCustomFields]);

  const columns = [
    {
      dataIndex: "criteria",
      editable: true,
      inputType: "select",
      title: "Criteria",
      options: criteriaOptions,
      render: (value, record) => {
        if (value && value.startsWith("debtor_udf:")) {
          const fieldSlug = value.split(":")[1];
          return getCustomFieldDisplayName(fieldSlug);
        }

        if (value === "debtor_udf" && record.fieldName) {
          return getCustomFieldDisplayName(record.fieldName);
        }

        const field = thirdPriorityFields.find((f) => f.value === value);
        return field?.label || value;
      },
      rules: [
        {
          required: true,
          message: "Select Criteria",
        },
      ],
    },
    {
      dataIndex: "weight",
      editable: true,
      inputType: "number",
      title: "Weight",
      rules: [
        {
          required: true,
          message: "Enter Weight",
        },
      ],
    },
  ];

  const packetingSteps = [
    {
      title: "Basic Rule Info",
      form: (
        <>
          <StyledRow>
            <StyledContent>
              <Form.Item
                name="name"
                label="Name"
                rules={[{ required: true, message: "This field is required." }]}
              >
                <Input />
              </Form.Item>
            </StyledContent>
          </StyledRow>
          <StyledRow>
            <StyledContent>
              <Form.Item label="Description" name="notes">
                <Input />
              </Form.Item>
            </StyledContent>
          </StyledRow>
        </>
      ),
    },
    {
      title: "Identifier Match Params",
      form: (
        <>
          {firstPriorityFields.map((field) => (
            <StyledRow key={field.value}>
              <StyledContent>
                <Form.Item
                  valuePropName="checked"
                  key={field.value}
                  name={["identifierMatchParams", field.value]}
                >
                  <Checkbox>{field.label}</Checkbox>
                </Form.Item>
              </StyledContent>
            </StyledRow>
          ))}
        </>
      ),
    },
    {
      title: "Identifier Disqualifying Params",
      form: (
        <>
          {secondPriorityFields.map((field) => (
            <StyledRow key={field.value}>
              <StyledContent>
                <Form.Item
                  valuePropName="checked"
                  key={field.value}
                  name={["disqualifyingMatchParams", field.value]}
                >
                  <Checkbox>{field.label}</Checkbox>
                </Form.Item>
              </StyledContent>
            </StyledRow>
          ))}
        </>
      ),
    },
    {
      title: "Identifier Scoring",
      form: (
        <TableFormList
          addText="Add Criteria"
          defaultValues={{ criteria: criteriaOptions?.[0]?.value }}
          columns={columns}
          data={criterias}
          onDelete={handleDeleteCriteria}
          onSave={handleAddCriteria}
        />
      ),
      wrapperProps: {
        style: {
          width: "100%",
          maxWidth: "100%",
        },
      },
    },
  ];

  return (
    <Modal
      maskClosable={false}
      title={title}
      onOk={onOk}
      onCancel={onCancel}
      footer={null}
      width={1000}
      open
    >
      <FormWizard
        canSkipSteps={!!activeRecord}
        steps={packetingSteps}
        onCancel={onCancel}
        onDone={onSubmitForm}
        initialValues={initialValues}
      />
    </Modal>
  );
}
